// src/components/admin/AdminChat.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import apiService from "../../services/ApiServices";
import {
  Container, Row, Col, Card, Table, Button, Form, InputGroup, Modal, Pagination,
  Spinner, Alert, Badge
} from "react-bootstrap";
import {
  FaUniversity, FaSearch, FaArrowLeft, FaComments,
  FaUserShield, FaPaperclip, FaRoute, FaUsers, FaSort, FaSortUp, FaSortDown, FaDownload
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import "./AdminChat.css";
import "./AddDaysLeft.css";
import { format, differenceInCalendarDays } from "date-fns";

let collegesRequestPromise = null;
let collegesRequestCache = null;

const renderMessageWithLinks = (text) => {
  if (!text) return "";
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = text.split(urlRegex);
  if (parts.length === 1) {
    return text;
  }
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      const href = part.toLowerCase().startsWith("http") ? part : `https://${part}`;
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline", wordBreak: "break-all" }}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const normalizeChatPayload = (chat = {}) => ({
  ...chat,
  chat_id: chat.chat_id || chat.id || chat.message_id || chat.notification_id || null,
  sender_id: Number(
    chat.sender_id ??
    chat.senderId ??
    chat.from_user_id ??
    chat.user_id ??
    chat.sender_user?.user_id ??
    chat.senderUser?.user_id ??
    0
  ),
  receiver_id: Number(
    chat.receiver_id ??
    chat.receiverId ??
    chat.to_user_id ??
    chat.target_user_id ??
    chat.receiver_user?.user_id ??
    chat.receiverUser?.user_id ??
    0
  ),
  student_id: Number(chat.student_id ?? chat.studentId ?? chat.user_id ?? 0) || null,
  staff_id: Number(chat.staff_id ?? chat.staffId ?? chat.receiver_id ?? chat.receiverId ?? 0) || null,
  chat_type: chat.chat_type || chat.chatType || chat.type || "individual",
  message: chat.message ?? chat.text ?? chat.content ?? "",
  sent_at: chat.sent_at || chat.sentAt || chat.created_at || chat.createdAt || new Date().toISOString(),
  sender_user: chat.sender_user || chat.senderUser || null,
  receiver_user: chat.receiver_user || chat.receiverUser || null,
  sender_name: chat.sender_name || chat.senderUser?.name || chat.sender_user?.name || chat.name || "",
  receiver_name: chat.receiver_name || chat.receiverUser?.name || chat.receiver_user?.name || "",
  sender_role: chat.sender_role || chat.senderRole || chat.sender_user?.role || chat.senderUser?.role || null,
  receiver_role: chat.receiver_role || chat.receiverRole || chat.receiver_user?.role || chat.receiverUser?.role || null,
});

const buildMessageIdentity = (message = {}) =>
  [
    message.chat_id || "",
    Number(message.sender_id || 0),
    Number(message.receiver_id || 0),
    Number(message.student_id || 0),
    Number(message.staff_id || 0),
    message.chat_type || "individual",
    message.message || "",
    message.sent_at || "",
  ].join("|");

const isMessageForCurrentThread = ({
  message = {},
  selectedUser,
  receiverId,
  chatMode,
  senderId,
  currentView,
  isHistoryView,
  historyStaffId,
}) => {
  const activeStudentId = Number(selectedUser?.student_id || selectedUser?.user_id || 0);
  const activeReceiverId = Number(receiverId || 0);
  const incomingSenderId = Number(message.sender_id || 0);
  const incomingReceiverId = Number(message.receiver_id || 0);
  const activeHistoryStaffId = Number(historyStaffId || activeReceiverId || senderId || 0);

  if (chatMode === "group") {
    if (!selectedUser) return false;
    return (
      message.chat_type === "group" &&
      Number(message.student_id || activeStudentId || 0) === activeStudentId
    );
  }

  if (!selectedUser) return false;

  const directPairMatch =
    (incomingSenderId && incomingReceiverId &&
      ((incomingSenderId === Number(senderId) && incomingReceiverId === activeReceiverId) ||
        (incomingSenderId === activeReceiverId && incomingReceiverId === Number(senderId))));

  const studentStaffMatch =
    Number(message.student_id || 0) === activeStudentId &&
    Number(message.staff_id || 0) === activeHistoryStaffId;

  if (currentView === "chat") {
    return message.chat_type !== "group" && (directPairMatch || studentStaffMatch);
  }

  return message.chat_type !== "group" && (directPairMatch || studentStaffMatch || isHistoryView);
};

const AdminChat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const senderId = storedUser?.user_id || "";
  const defaultCollegeId = storedUser?.college_id || "";
  const notificationParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      chatType: params.get("chatType") || "",
      studentId: params.get("studentId") || "",
      staffId: params.get("staffId") || "",
      collegeId: params.get("collegeId") || "",
    };
  }, [location.search]);

  const [socketStatus, setSocketStatus] = useState("disconnected");
  const [receiverId, setReceiverId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const USERS_PER_PAGE = 10;

  // API Filters
  const [filterName, setFilterName] = useState("");
  const [filterUserName, setFilterUserName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterPhoneNumber, setFilterPhoneNumber] = useState("");
  const [filterAccountStatus, setFilterAccountStatus] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterActivityStatus, setFilterActivityStatus] = useState("");

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1 text-muted" style={{ fontSize: '0.75rem', opacity: 0.5 }} />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="ms-1 text-primary" style={{ fontSize: '0.85rem' }} />
      : <FaSortDown className="ms-1 text-primary" style={{ fontSize: '0.85rem' }} />;
  };

  // Bulk messaging states
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState("");
  const [bulkError, setBulkError] = useState("");

  const handleSendBulkMessage = async () => {
    if (selectedUserIds.length === 0 || !bulkMessage.trim()) return;

    try {
      setBulkSending(true);
      setBulkSuccess("");
      setBulkError("");

      if (!socketRef.current || socketStatus !== "connected") {
        setBulkError("Socket is not connected. Please try again.");
        setBulkSending(false);
        return;
      }

      selectedUserIds.forEach((userId) => {
        const user = users.find(u => Number(u.user_id) === Number(userId));
        const collegeIdValue = user?.college_id || selectedCollege?.college_id || null;

        // Emit message through socket
        socketRef.current.emit("chat:send", {
          sender_id: Number(senderId),
          receiver_id: Number(userId),
          message: bulkMessage.trim(),
          college_id: collegeIdValue && collegeIdValue !== 'all' ? Number(collegeIdValue) : null,
          url: null,
          chat_type: "group",
          student_id: Number(userId),
          staff_id: Number(senderId),
          sender_user: {
            user_id: Number(senderId),
            name: storedUser?.name || storedUser?.user_name || "You",
            role_id: storedUser?.role_id,
          },
        });
      });

      setBulkSuccess(`Successfully sent message to ${selectedUserIds.length} users via socket.`);
      setBulkMessage("");
      setTimeout(() => {
        setBulkSuccess("");
      }, 5000);
    } catch (err) {
      setBulkError("Failed to send bulk message: " + err.message);
    } finally {
      setBulkSending(false);
    }
  };

  const [chatMode, setChatMode] = useState("individual");
  const [groupLabel, setGroupLabel] = useState("");
  const [groupMembers, setGroupMembers] = useState([]);
  const [isHistoryView, setIsHistoryView] = useState(false);
  const [historyStaffId, setHistoryStaffId] = useState("");

  // College selection states
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [collegeSearch, setCollegeSearch] = useState("");
  const [pendingNotificationTarget, setPendingNotificationTarget] = useState(null);

  // View states
  const [currentView, setCurrentView] = useState("college");

  // Staff list for history (Erish admin)
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffUsersErish, setStaffUsersErish] = useState([]);
  const [staffUsersCollege, setStaffUsersCollege] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");
  const [staffScope, setStaffScope] = useState("college");

  // File upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFilePreview, setSelectedFilePreview] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");
  const fileInputRef = useRef(null); // ← Fixed: Use ref for file input

  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extraDays, setExtraDays] = useState(30);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  const [extensionMode, setExtensionMode] = useState("days");
  const [targetDate, setTargetDate] = useState("");
  const isMountedRef = useRef(false);

  const socketRef = useRef(null);
  const scrollRef = useRef(null);
  const receiverIdRef = useRef(receiverId);
  const chatModeRef = useRef(chatMode);
  const currentViewRef = useRef(currentView);
  const selectedUserRef = useRef(selectedUser);
  const historyStaffIdRef = useRef(historyStaffId);
  const isHistoryViewRef = useRef(isHistoryView);

  const socketUrl = process.env.REACT_APP_SOCKET_URL || "https://api.japaking.online";
  const isErishAdmin = Number(storedUser?.role_id) === 6;

  const getDaysLeft = (endDate) => {
    if (!endDate) return 0;
    const days = differenceInCalendarDays(new Date(endDate), new Date());
    return days > 0 ? days : 0;
  };

  const handleExtendClick = () => {
    if (!selectedUser) return;
    setExtraDays(30);
    setExtensionMode("days");
    setTargetDate("");
    setShowExtendModal(true);
  };

  const handleExtendSubmit = async () => {
    if (!selectedUser) return;
    if (extensionMode === "days" && extraDays <= 0) return;
    if (extensionMode === "date" && !targetDate) return;

    try {
      setUpdatingSubscription(true);
      setErrorMessage("");

      let newEnd;
      if (extensionMode === "days") {
        newEnd = new Date();
        newEnd.setDate(newEnd.getDate() + Number(extraDays));
      } else {
        newEnd = new Date(targetDate);
      }

      const response = await apiService.patch(
        `/api/user-admin/subscriptions/${selectedUser.user_id}`,
        { end_date: newEnd.toISOString() }
      );

      if (response?.success) {
        setShowExtendModal(false);
        await loadUsersForCollege(selectedCollege?.college_id ?? null);
        setSelectedUser((prev) =>
          prev ? { ...prev, end_date: newEnd.toISOString() } : prev
        );
      } else {
        setErrorMessage("Failed to extend subscription.");
      }
    } catch (error) {
      setErrorMessage("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setUpdatingSubscription(false);
    }
  };

  useEffect(() => {
    receiverIdRef.current = receiverId;
  }, [receiverId]);

  useEffect(() => {
    chatModeRef.current = chatMode;
  }, [chatMode]);

  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    historyStaffIdRef.current = historyStaffId;
  }, [historyStaffId]);

  useEffect(() => {
    isHistoryViewRef.current = isHistoryView;
  }, [isHistoryView]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!notificationParams.chatType) return;

    if (notificationParams.collegeId) {
      const collegeIdNumber = Number(notificationParams.collegeId);
      if (Number.isFinite(collegeIdNumber) && collegeIdNumber > 0) {
        setSelectedCollege((prev) =>
          prev?.college_id === collegeIdNumber
            ? prev
            : { college_id: collegeIdNumber }
        );
      }
    }

    if (notificationParams.chatType === "group" || notificationParams.chatType === "individual") {
      setChatMode(notificationParams.chatType);
      setPendingNotificationTarget({
        chatType: notificationParams.chatType,
        studentId: notificationParams.studentId || "",
        staffId: notificationParams.staffId || "",
      });
    }
  }, [notificationParams.chatType, notificationParams.studentId, notificationParams.staffId, notificationParams.collegeId]);

  // Scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    return () => {
      if (selectedFilePreview) URL.revokeObjectURL(selectedFilePreview);
    };
  }, [selectedFilePreview]);

  // Fetch colleges
  const fetchColleges = useCallback(async () => {
    try {
      if (collegesRequestCache) {
        if (isMountedRef.current && Array.isArray(collegesRequestCache)) {
          setColleges(collegesRequestCache);
        }
        return;
      }

      if (!collegesRequestPromise) {
        collegesRequestPromise = apiService.get('/api/college').then((response) => {
          const collegesList =
            response?.success && Array.isArray(response?.message?.showCollege)
              ? response.message.showCollege
              : [];
          collegesRequestCache = collegesList;
          return collegesList;
        }).finally(() => {
          collegesRequestPromise = null;
        });
      }

      const collegesList = await collegesRequestPromise;
      if (isMountedRef.current) {
        setColleges(collegesList);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setErrorMessage("Failed to load colleges.");
      }
    }
  }, []);

  // Load users with subscription dates
  const loadUsersForCollege = useCallback(async (collegeValue, customFilters = null) => {
    try {
      setUsersLoading(true);
      const params = {};
      if (collegeValue && collegeValue !== "all") {
        params.collegeId = collegeValue;
      } else if (collegeValue === "all") {
        params.collegeId = "all";
      }

      const filters = customFilters || {
        name: filterName,
        userName: filterUserName,
        email: filterEmail,
        phoneNumber: filterPhoneNumber,
        accountStatus: filterAccountStatus,
        status: filterStatus,
        activityStatus: filterActivityStatus,
      };

      if (filters.name) params.name = filters.name;
      if (filters.userName) params.userName = filters.userName;
      if (filters.email) params.email = filters.email;
      if (filters.phoneNumber) params.phoneNumber = filters.phoneNumber;
      if (filters.accountStatus) params.accountStatus = filters.accountStatus;
      if (filters.status) params.status = filters.status;
      if (filters.activityStatus) params.activityStatus = filters.activityStatus;

      const response = await apiService.get("/api/user-admin/subscriptions", params);
      if (response?.success && Array.isArray(response?.message?.users)) {
        const filtered = response.message.users.filter(
          (user) => user.user_id !== Number(senderId)
        );
        setUsers(filtered);
        setGroupMembers(filtered);
      } else {
        setUsers([]);
        setGroupMembers([]);
      }
    } catch (error) {
      setErrorMessage("Failed to load users.");
    } finally {
      setUsersLoading(false);
    }
  }, [
    senderId,
    filterName,
    filterUserName,
    filterEmail,
    filterPhoneNumber,
    filterAccountStatus,
    filterStatus,
    filterActivityStatus
  ]);

  useEffect(() => {
    if (!notificationParams.collegeId) return;

    const collegeIdNumber = Number(notificationParams.collegeId);
    if (!Number.isFinite(collegeIdNumber) || collegeIdNumber <= 0) return;

    setCurrentView("users");
    setSelectedCollege({ college_id: collegeIdNumber });
    loadUsersForCollege(collegeIdNumber, {});
  }, [notificationParams.collegeId, loadUsersForCollege]);

  // Initial load
  // Handle college selection
  const handleSelectCollege = (college) => {
    setSelectedCollege(college);
    setCurrentView("users");
    setChatMode("individual");
    setReceiverId("");
    setSelectedUser(null);
    setMessages([]);

    setFilterName("");
    setFilterUserName("");
    setFilterEmail("");
    setFilterPhoneNumber("");
    setFilterAccountStatus("");
    setFilterStatus("");
    setFilterActivityStatus("");

    if (college) {
      if (college.college_id === "all") {
        setGroupLabel("All Users Group");
        loadUsersForCollege("all", {});
      } else {
        setGroupLabel(`${college.college_name || "College"} Group`);
        loadUsersForCollege(college.college_id, {});
      }
    } else {
      setGroupLabel("Erish Admin Group");
      loadUsersForCollege(null, {});
    }
  };

  // Back to colleges
  const handleBackToColleges = () => {
    setCurrentView("college");
    setSelectedCollege(null);
    setUsers([]);
    setReceiverId("");
    setMessages([]);
    setSelectedUser(null);
    setChatMode("individual");
    setIsHistoryView(false);
    setHistoryStaffId("");
    clearSelectedFile();
    setDraft("");
  };

  // Back to users
  const handleBackToUsers = () => {
    setCurrentView("users");
    setReceiverId("");
    setMessages([]);
    setSelectedUser(null);
    setChatMode("individual");
    setIsHistoryView(false);
    setHistoryStaffId("");
    clearSelectedFile();
    setDraft("");
  };

  // Select user for chat
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setReceiverId(user.user_id.toString());
    setChatMode("individual");
    setCurrentView("details");
    setIsHistoryView(false);
    setHistoryStaffId("");
    setDraft("");
    clearSelectedFile();
  };

  const handleOpenGroupChat = () => {
    if (!selectedUser) return;
    setChatMode("group");
    setReceiverId(selectedUser.user_id.toString());
    setMessages([]);
    setDraft("");
    clearSelectedFile();
    setIsHistoryView(false);
    setCurrentView("chat");
    fetchHistory(selectedUser.student_id || selectedUser.user_id, "group");
  };

  const handleOpenIndividualChat = () => {
    if (!selectedUser) return;
    setChatMode("individual");
    setReceiverId(selectedUser.user_id.toString());
    setMessages([]);
    setDraft("");
    clearSelectedFile();
    setIsHistoryView(false);
    setCurrentView("chat");
    fetchHistory(selectedUser.student_id || selectedUser.user_id, "individual");
  };

  // Fetch history (student_id based)
  const fetchHistory = useCallback(async (studentValue, type = "individual", staffValue = null) => {
    if (!studentValue || !senderId) return;
    try {
      setHistoryLoading(true);
      const params = {
        studentId: Number(studentValue),
        chatType: type,
      };
      if (type === "individual") {
        params.staffId = Number(staffValue || senderId);
      }
      const response = await apiService.get("/api/chat-rt/history", params);
      if (response?.success && Array.isArray(response?.message?.history)) {
        setMessages(response.message.history);
      } else {
        setMessages([]);
      }
    } catch (error) {
      setErrorMessage("Failed to load chat history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [senderId]);

  useEffect(() => {
    if (!senderId) {
      setErrorMessage("Missing sender id. Please login again.");
      return;
    }

    if (defaultCollegeId) {
      setSelectedCollege({ college_id: defaultCollegeId });
      setGroupLabel("College Group");
      setCurrentView("users");
      loadUsersForCollege(defaultCollegeId, {});
    } else {
      setCurrentView("college");
      fetchColleges();
    }

    // Socket connection
    const socket = io(socketUrl, {
      transports: ["websocket"],
      withCredentials: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setSocketStatus("connected");
      socket.emit("join", { user_id: senderId, userId: senderId, id: senderId });
    });

    socket.on("disconnect", () => setSocketStatus("disconnected"));
    socket.on("connect_error", (error) => {
      setSocketStatus("disconnected");
      setErrorMessage(`Socket connection failed: ${error?.message || "Unknown error"}`);
    });

    const appendIfRelevant = (incoming) => {
      const chat = normalizeChatPayload(incoming);
      if (!isMessageForCurrentThread({
        message: chat,
        selectedUser: selectedUserRef.current,
        receiverId: receiverIdRef.current,
        chatMode: chatModeRef.current,
        senderId,
        currentView: currentViewRef.current,
        isHistoryView: isHistoryViewRef.current,
        historyStaffId: historyStaffIdRef.current,
      })) {
        return;
      }

      setMessages((prev) => {
        if (prev.some((m) => buildMessageIdentity(m) === buildMessageIdentity(chat) || m.chat_id === chat.chat_id)) {
          return prev;
        }
        return [...prev, chat];
      });
    };

    const handleIncomingChat = (chat) => {
      const normalizedChat = normalizeChatPayload(chat);
      appendIfRelevant(normalizedChat);
    };

    const handleIncomingNotification = (notification) => {
      const normalizedChat = normalizeChatPayload({
        chat_id: notification?.chat_id || notification?.related_id || notification?.id,
        sender_id: notification?.sender_id || notification?.user_id || notification?.sender_user?.user_id,
        receiver_id: notification?.receiver_id || notification?.target_user_id || notification?.receiver_user?.user_id,
        student_id: notification?.student_id || notification?.studentId || notification?.sender_user?.student_id || notification?.receiver_user?.student_id,
        staff_id: notification?.staff_id || notification?.staffId || notification?.sender_user?.staff_id || notification?.receiver_user?.staff_id,
        chat_type: notification?.chat_type || notification?.chatType || notification?.notification_type || "individual",
        message: notification?.message || notification?.body || "New message",
        sent_at: notification?.created_at || notification?.createdAt || new Date().toISOString(),
        sender_user: notification?.sender_user || notification?.senderUser || null,
        receiver_user: notification?.receiver_user || notification?.receiverUser || null,
        sender_name: notification?.sender_name || notification?.sender_user?.name || notification?.senderUser?.name || "",
        receiver_name: notification?.receiver_name || notification?.receiver_user?.name || notification?.receiverUser?.name || "",
        sender_role: notification?.sender_role || notification?.sender_user?.role || notification?.senderUser?.role || null,
        receiver_role: notification?.receiver_role || notification?.receiver_user?.role || notification?.receiverUser?.role || null,
      });

      appendIfRelevant(normalizedChat);
    };

    ["chat:new", "chat:sent", "message:new", "message:sent", "message:received"].forEach((eventName) => {
      socket.on(eventName, handleIncomingChat);
    });

    ["notification:new", "notifications:new", "notification:created"].forEach((eventName) => {
      socket.on(eventName, handleIncomingNotification);
    });

    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderId, defaultCollegeId, socketUrl]);

  const loadStaffUsers = async (collegeId) => {
    try {
      setStaffLoading(true);
      const [erishResponse, collegeResponse] = await Promise.all([
        apiService.get("/api/user-admin/non-students"),
        collegeId
          ? apiService.get(`/api/user-admin/non-students?collegeId=${collegeId}`)
          : Promise.resolve(null),
      ]);

      const erishList =
        erishResponse?.success && Array.isArray(erishResponse?.message?.users)
          ? erishResponse.message.users.filter((user) => user.college_id == null)
          : [];

      const collegeList =
        collegeResponse?.success && Array.isArray(collegeResponse?.message?.users)
          ? collegeResponse.message.users.filter(
            (user) => user.college_id === Number(collegeId)
          )
          : [];

      const mergedDefault = collegeId ? collegeList : erishList;
      setStaffUsersErish(erishList);
      setStaffUsersCollege(collegeList);
      setStaffUsers(mergedDefault);
    } catch (error) {
      setErrorMessage("Failed to load staff users.");
    } finally {
      setStaffLoading(false);
    }
  };

  const handleOpenHistoryView = () => {
    if (!selectedUser || !historyStaffId) return;
    const studentId = selectedUser.student_id || selectedUser.user_id;
    setChatMode("individual");
    setReceiverId(historyStaffId.toString());
    setMessages([]);
    setDraft("");
    clearSelectedFile();
    setIsHistoryView(true);
    setCurrentView("chat");
    fetchHistory(studentId, "individual", historyStaffId);
  };

  useEffect(() => {
    if (!isErishAdmin || !selectedUser) return;
    const collegeId = selectedCollege?.college_id || null;
    loadStaffUsers(collegeId);
    if (collegeId) {
      setStaffScope("college");
    } else {
      setStaffScope("erish");
    }
  }, [isErishAdmin, selectedUser, selectedCollege]);

  useEffect(() => {
    if (staffScope === "college") {
      setStaffUsers(staffUsersCollege);
    } else {
      setStaffUsers(staffUsersErish);
    }
    setHistoryStaffId("");
  }, [staffScope, staffUsersCollege, staffUsersErish]);

  // Handle file upload (fixed trigger)
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (selectedFilePreview) URL.revokeObjectURL(selectedFilePreview);
    setSelectedFile(file);
    setSelectedFilePreview(URL.createObjectURL(file));
    setSelectedFileType(file.type || "");
  };

  const clearSelectedFile = () => {
    if (selectedFilePreview) URL.revokeObjectURL(selectedFilePreview);
    setSelectedFile(null);
    setSelectedFilePreview("");
    setSelectedFileType("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Trigger file input on paperclip click (fixed)
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Send message (text via socket, file via POST)
  const handleSend = async () => {
    if (!draft.trim() && !selectedFile) {
      setErrorMessage("Write a message or attach a file.");
      return;
    }

    if (!receiverId) {
      setErrorMessage("Select a user to chat.");
      return;
    }

    try {
      if (selectedFile) {
        const formData = new FormData();
        formData.append("sender_id", Number(senderId).toString());
        if (receiverId) {
          formData.append("receiver_id", Number(receiverId).toString());
        }
        formData.append("message", draft.trim());
        formData.append("chat_type", chatMode);
        if (defaultCollegeId) {
          formData.append("college_id", Number(defaultCollegeId).toString());
        }
        formData.append("file", selectedFile);

        const response = await apiService.post("/api/chat-rt/send", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (!response?.success) {
          setErrorMessage("Failed to send message with file.");
          return;
        }

        if (receiverId) {
          const studentId = selectedUser?.student_id || selectedUser?.user_id || receiverId;
          await fetchHistory(studentId, chatMode);
        }
      } else {
        socketRef.current?.emit("chat:send", {
          sender_id: Number(senderId),
          receiver_id: receiverId ? Number(receiverId) : null,
          message: draft.trim(),
          college_id: defaultCollegeId ? Number(defaultCollegeId) : null,
          url: null,
          chat_type: chatMode,
          student_id: selectedUser?.student_id || selectedUser?.user_id || null,
          staff_id: receiverId ? Number(receiverId) : null,
          sender_user: {
            user_id: Number(senderId),
            name: storedUser?.name || storedUser?.user_name || "You",
            role_id: storedUser?.role_id,
          },
        });
      }

      setDraft("");
      clearSelectedFile();
    } catch (error) {
      setErrorMessage(
        "Error sending message: " + (error.message || "Unknown error")
      );
    }
  };

  // Filter colleges
  const filteredColleges = colleges.filter(college =>
    college.college_name?.toLowerCase().includes(collegeSearch.toLowerCase()) ||
    college.college_code?.toLowerCase().includes(collegeSearch.toLowerCase())
  );

  // Memoized sorted users list
  const sortedUsers = useMemo(() => {
    let sortableUsers = [...users];

    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle null/undefined values
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        // If sorting dates
        if (['start_date', 'end_date', 'last_study_date'].includes(sortConfig.key)) {
          const aTime = aVal ? new Date(aVal).getTime() : 0;
          const bTime = bVal ? new Date(bVal).getTime() : 0;
          return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
        }

        // If sorting numbers
        if (['access_count_7_days'].includes(sortConfig.key)) {
          const aNum = Number(aVal) || 0;
          const bNum = Number(bVal) || 0;
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // Default string comparison
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aStr > bStr) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  const downloadCSV = () => {
    if (sortedUsers.length === 0) return;

    const headers = [
      'Name',
      'Phone Number',
      'Email',
      'Username',
      'College Name',
      'Status',
      'Account Status',
      'Activity Status',
      'Expiration Date'
    ];

    const csvRows = [headers.join(',')];

    for (const user of sortedUsers) {
      const rowData = [
        `"${(user.name || '').replace(/"/g, '""')}"`,
        `"${(user.phone_number || '').replace(/"/g, '""')}"`,
        `"${(user.email || '').replace(/"/g, '""')}"`,
        `"${(user.user_name || '').replace(/"/g, '""')}"`,
        `"${(user.college_name || 'Non-College').replace(/"/g, '""')}"`,
        `"${(user.status || '').replace(/"/g, '""')}"`,
        `"${(user.account_status || '').replace(/"/g, '""')}"`,
        `"${((user.Activity_Status === 'Trail' || user.Activity_Status === 'trail') ? 'Trial' : (user.Activity_Status || '')).replace(/"/g, '""')}"`,
        `"${user.end_date ? format(new Date(user.end_date), 'dd MMM yyyy') : ''}"`
      ];
      csvRows.push(rowData.join(','));
    }

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);

    const dateStr = format(new Date(), 'dd-MM-yyyy');
    const collegeSanitized = (selectedCollege?.college_name || 'All-Users').replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute('download', `chat_users_${collegeSanitized}_${dateStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalUserPages = Math.max(1, Math.ceil(sortedUsers.length / USERS_PER_PAGE));
  const paginatedUsers = sortedUsers.slice(
    (userPage - 1) * USERS_PER_PAGE,
    userPage * USERS_PER_PAGE
  );

  const expiringSoonCount = sortedUsers.filter((user) => getDaysLeft(user.end_date) <= 7).length;

  const resolvedSelectedUser = useMemo(() => {
    if (!selectedUser) return null;
    const fromList = users.find((user) => Number(user.user_id) === Number(selectedUser.user_id));
    return {
      ...(fromList || {}),
      ...selectedUser,
      ...(selectedUser?.role ? { role: selectedUser.role } : {}),
      phone_number: selectedUser?.phone_number || fromList?.phone_number || "",
      birthdate: selectedUser?.birthdate || fromList?.birthdate || "",
    };
  }, [selectedUser, users]);

  const getSelectedUserName = () =>
    resolvedSelectedUser?.name || resolvedSelectedUser?.user_name || "User";



  const getHistoryStaffName = () => {
    const staff = staffUsers.find((u) => u.user_id === Number(historyStaffId));
    return staff?.name || staff?.user_name || `Staff ${historyStaffId}`;
  };

  const getSenderDesignation = (record, msg = {}) => {
    const roleName =
      record?.role?.role_name ||
      record?.role_name ||
      msg?.sender_role?.role_name ||
      msg?.sender_role?.role ||
      msg?.receiver_role?.role_name ||
      "";
    const roleLower = roleName.toLowerCase();
    if (roleLower.includes("student") || Number(record?.role_id) === 1) return "Student";
    if (roleLower.includes("staff") || roleLower.includes("admin")) return "Staff";
    return "Staff";
  };

  const getSenderRoleName = (record, msg = {}) => {
    return (
      record?.role?.role_name ||
      record?.role_name ||
      msg?.sender_role?.role_name ||
      "User"
    );
  };

  const getSenderDisplayName = (record, msg = {}, fallbackId) =>
    record?.name ||
    msg?.sender_name ||
    msg?.sender_user?.name ||
    record?.user_name ||
    (fallbackId ? `User ${fallbackId}` : "User");

  const getSenderLabel = (msg) => {
    const senderRecord =
      msg?.sender_user ||
      users.find((u) => Number(u.user_id) === Number(msg.sender_id)) ||
      staffUsers.find((u) => Number(u.user_id) === Number(msg.sender_id)) ||
      (Number(msg.sender_id) === Number(senderId)
        ? storedUser
        : null) ||
      null;

    const designation = getSenderDesignation(senderRecord, msg);
    const senderRole = getSenderRoleName(senderRecord, msg);

    if (designation === "Staff") {
      return senderRole === "User" ? "Staff" : senderRole;
    }
    return designation; // "Student"
  };

  const handleViewLearningJourney = (user) => {
    const search = new URLSearchParams({
      user_id: String(user.user_id || ""),
      student_id: String(user.student_id || user.user_id || ""),
      name: user.name || user.user_name || "",
      email: user.email || "",
      college_id: String(user.college_id || selectedCollege?.college_id || ""),
    });

    navigate({
      pathname: "/admin-chat/learning-journey",
      search: `?${search.toString()}`,
    }, {
      state: { user },
    });
  };

  useEffect(() => {
    setUserPage(1);
  }, [
    filterName,
    filterUserName,
    filterEmail,
    filterPhoneNumber,
    filterAccountStatus,
    filterStatus,
    filterActivityStatus,
    selectedCollege?.college_id,
    currentView
  ]);

  useEffect(() => {
    if (userPage > totalUserPages) {
      setUserPage(totalUserPages);
    }
  }, [userPage, totalUserPages]);

  useEffect(() => {
    if (!pendingNotificationTarget?.studentId || users.length === 0) return;

    const matchedUser = users.find((user) => {
      const userKey = String(user.student_id || user.user_id || "");
      return userKey === String(pendingNotificationTarget.studentId);
    });

    if (!matchedUser) return;

    setSelectedUser((prev) =>
      prev?.user_id === matchedUser.user_id ? prev : matchedUser
    );
    setReceiverId(String(matchedUser.user_id));

    if (pendingNotificationTarget.chatType === "group") {
      setCurrentView("chat");
      setChatMode("group");
      fetchHistory(matchedUser.student_id || matchedUser.user_id, "group");
    } else {
      setCurrentView("chat");
      setChatMode("individual");
      fetchHistory(
        matchedUser.student_id || matchedUser.user_id,
        "individual",
        pendingNotificationTarget.staffId || senderId
      );
    }

    setPendingNotificationTarget(null);
  }, [pendingNotificationTarget, users, senderId]);

  if (!senderId) {
    return <div className="text-center py-5">Please login to access chat</div>;
  }

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">
        <FaComments className="me-2 text-primary" />
        Admin Chat
      </h2>

      {/* College Selection View */}
      {currentView === "college" && (
        <>
          <InputGroup className="mb-4">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search college by name or code..."
              value={collegeSearch}
              onChange={(e) => setCollegeSearch(e.target.value)}
            />
          </InputGroup>

          <Row>
            {/* All Users Tile */}
            <Col md={4} lg={3} className="mb-4">
              <Card
                className="shadow-sm text-center cursor-pointer admin-tile-card"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSelectCollege({ college_id: 'all', college_name: 'All Users' })}
              >
                <Card.Body className="d-flex flex-column justify-content-center">
                  <FaUsers size={50} className="text-primary mb-3 mx-auto" />
                  <h5>All Users</h5>
                  <p className="text-muted small">All registered users</p>
                </Card.Body>
              </Card>
            </Col>

            {/* Non-College Tile */}
            <Col md={4} lg={3} className="mb-4">
              <Card
                className="shadow-sm text-center cursor-pointer admin-tile-card"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSelectCollege(null)}
              >
                <Card.Body className="d-flex flex-column justify-content-center">
                  <FaUserShield size={50} className="text-primary mb-3 mx-auto" />
                  <h5>Non-College / System Users</h5>
                  <p className="text-muted small">Global users</p>
                </Card.Body>
              </Card>
            </Col>

            {/* College Tiles */}
            {filteredColleges.map((college) => (
              <Col md={4} lg={3} key={college.college_id} className="mb-4">
                <Card
                  className="shadow-sm text-center cursor-pointer admin-tile-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSelectCollege(college)}
                >
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <FaUniversity size={50} className="text-primary mb-3 mx-auto" />
                    <h5>{college.college_name}</h5>
                    {college.college_code && (
                      <Badge bg="info" className="mx-auto">{college.college_code}</Badge>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* Users List View */}
      {currentView === "users" && (
        <>
          {!defaultCollegeId && (
            <Button
              variant="outline-secondary"
              className="mb-4"
              onClick={handleBackToColleges}
            >
              <FaArrowLeft className="me-2" /> Back to Colleges
            </Button>
          )}

          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <h4 className="mb-0">
              Users of <strong>{selectedCollege?.college_name || "System"}</strong>
            </h4>
            {sortedUsers.length > 0 && (
              <Button
                variant="success"
                onClick={downloadCSV}
                className="d-flex align-items-center gap-2 csv-download-btn"
              >
                <FaDownload /> Download CSV
              </Button>
            )}
          </div>

          <Card className="border-0 shadow-sm mb-4 filter-section-card">
            <Card.Body>
              <h5 className="mb-3 text-muted small fw-bold text-uppercase">Filter Students</h5>
              <Form onSubmit={(e) => { e.preventDefault(); loadUsersForCollege(selectedCollege?.college_id ?? null); }}>
                <Row className="g-3">
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by name..."
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Username</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by username..."
                        value={filterUserName}
                        onChange={(e) => setFilterUserName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Email</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by email..."
                        value={filterEmail}
                        onChange={(e) => setFilterEmail(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Phone Number</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by phone..."
                        value={filterPhoneNumber}
                        onChange={(e) => setFilterPhoneNumber(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Account Status</Form.Label>
                      <Form.Select
                        value={filterAccountStatus}
                        onChange={(e) => setFilterAccountStatus(e.target.value)}
                      >
                        <option value="">All Account Statuses</option>
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="rejected">Rejected</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Subscription Status</Form.Label>
                      <Form.Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="">All Subscription Statuses</option>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="pending">Pending</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Activity Status</Form.Label>
                      <Form.Select
                        value={filterActivityStatus}
                        onChange={(e) => setFilterActivityStatus(e.target.value)}
                      >
                        <option value="">All Activity Statuses</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Dormant">Dormant</option>
                        <option value="Trial">Trial</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3} className="d-flex align-items-end gap-2">
                    <Button variant="primary" type="submit" className="w-100 filter-btn">
                      Apply
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="w-100 filter-btn"
                      onClick={() => {
                        setFilterName('');
                        setFilterUserName('');
                        setFilterEmail('');
                        setFilterPhoneNumber('');
                        setFilterAccountStatus('');
                        setFilterStatus('');
                        setFilterActivityStatus('');
                        loadUsersForCollege(selectedCollege?.college_id ?? null, {});
                      }}
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {usersLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : users.length === 0 ? (
            <Card className="text-center p-5">
              <h5>No users found</h5>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm add-days-left-table-card mb-4">
              <Card.Body className="p-0">
                <div className="admin-chat-table-card__header">
                  <div>
                    <div className="admin-chat-table-card__eyebrow">User directory</div>
                    <h5 className="mb-1">Choose a student to start chatting</h5>
                    <div className="text-muted small">
                      Use the Chat action to open the selected student's thread.
                    </div>
                  </div>


                </div>

                <div className="table-responsive">
                  <Table hover className="align-middle mb-0 add-days-left-table">
                    <thead>
                      <tr>
                        <th className="sticky-col-idx" style={{ left: 0, width: '45px', minWidth: '45px', zIndex: 12, textAlign: 'center' }}>
                          <Form.Check
                            type="checkbox"
                            checked={sortedUsers.length > 0 && selectedUserIds.length === sortedUsers.length}
                            indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < sortedUsers.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUserIds(sortedUsers.map(u => u.user_id));
                              } else {
                                setSelectedUserIds([]);
                              }
                            }}
                          />
                        </th>
                        <th className="sticky-col-name" style={{ left: '45px', zIndex: 12 }} onClick={() => requestSort('name')}>
                          Name {getSortIcon('name')}
                        </th>
                        <th onClick={() => requestSort('phone_number')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Phone Number {getSortIcon('phone_number')}
                        </th>
                        <th onClick={() => requestSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Email {getSortIcon('email')}
                        </th>
                        <th onClick={() => requestSort('user_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Username {getSortIcon('user_name')}
                        </th>
                        <th onClick={() => requestSort('college_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          College Name {getSortIcon('college_name')}
                        </th>
                        <th onClick={() => requestSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Status {getSortIcon('status')}
                        </th>
                        <th onClick={() => requestSort('account_status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Account Status {getSortIcon('account_status')}
                        </th>
                        <th onClick={() => requestSort('Activity_Status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Activity Status {getSortIcon('Activity_Status')}
                        </th>
                        <th onClick={() => requestSort('start_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Joined (Start Date) {getSortIcon('start_date')}
                        </th>
                        <th onClick={() => requestSort('end_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Expiration Date {getSortIcon('end_date')}
                        </th>
                        <th onClick={() => requestSort('last_study_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Last Study Date {getSortIcon('last_study_date')}
                        </th>
                        <th onClick={() => requestSort('last_lesson')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Last Lesson {getSortIcon('last_lesson')}
                        </th>
                        <th onClick={() => requestSort('access_count_7_days')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Access (7 Days) {getSortIcon('access_count_7_days')}
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.length === 0 ? (
                        <tr>
                          <td colSpan="15">
                            <div className="admin-chat-table__empty">
                              <div className="admin-chat-table__empty-title">No matching users</div>
                              <div className="text-muted small">
                                Try a different search filter.
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedUsers.map((user, idx) => {
                          const daysLeft = getDaysLeft(user.end_date);
                          return (
                            <tr key={user.user_id}>
                              <td className="sticky-col-idx" style={{ left: 0, width: '45px', minWidth: '45px', textAlign: 'center' }}>
                                <Form.Check
                                  type="checkbox"
                                  checked={selectedUserIds.includes(user.user_id)}
                                  onChange={() => {
                                    setSelectedUserIds(prev => {
                                      if (prev.includes(user.user_id)) {
                                        return prev.filter(id => id !== user.user_id);
                                      } else {
                                        return [...prev, user.user_id];
                                      }
                                    });
                                  }}
                                />
                              </td>
                              <td className="sticky-col-name" style={{ left: '45px' }}><strong>{user.name}</strong></td>
                              <td>{user.phone_number || '—'}</td>
                              <td>{user.email || '—'}</td>
                              <td>{user.user_name || '—'}</td>
                              <td>{user.college_name || 'Non-College'}</td>
                              <td>
                                <Badge className={`badge-soft-status status-${(user.status || '').toLowerCase()}`}>
                                  {user.status || '—'}
                                </Badge>
                              </td>
                              <td>
                                <Badge className={`badge-soft-status status-${(user.account_status || '').toLowerCase()}`}>
                                  {user.account_status || '—'}
                                </Badge>
                              </td>
                              <td>
                                <Badge className={`badge-soft-activity activity-${(user.Activity_Status === 'Trail' || user.Activity_Status === 'trail' ? 'trial' : (user.Activity_Status || '').toLowerCase())}`}>
                                  {(user.Activity_Status === 'Trail' || user.Activity_Status === 'trail') ? 'Trial' : (user.Activity_Status || '—')}
                                </Badge>
                              </td>
                              <td>
                                {user.start_date ? format(new Date(user.start_date), 'dd MMM yyyy') : '—'}
                              </td>
                              <td>
                                {user.end_date ? format(new Date(user.end_date), 'dd MMM yyyy') : '—'}
                                <div className="small text-muted">
                                  ({daysLeft} days left)
                                </div>
                              </td>
                              <td>
                                {user.last_study_date ? format(new Date(user.last_study_date), 'dd MMM yyyy HH:mm') : '—'}
                              </td>
                              <td>{user.last_lesson || '—'}</td>
                              <td>
                                <Badge className="badge-soft-access" bg={Number(user.access_count_7_days) > 0 ? 'info' : 'light'} text={Number(user.access_count_7_days) > 0 ? 'white' : 'dark'}>
                                  {user.access_count_7_days || 0}
                                </Badge>
                              </td>
                              <td className="text-nowrap">
                                <div className="d-flex align-items-center gap-2">
                                  <Button
                                    variant="link"
                                    className="extend-action-btn text-primary p-0"
                                    onClick={() => handleSelectUser(user)}
                                  >
                                    <FaComments /> Chat
                                  </Button>
                                  <Button
                                    variant="link"
                                    className="extend-action-btn text-success p-0"
                                    onClick={() => handleViewLearningJourney(user)}
                                  >
                                    <FaRoute /> View Learning Journey
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>

                {sortedUsers.length > 0 && (
                  <div className="admin-chat-table-footer">
                    <div className="text-muted small">
                      Showing {Math.min((userPage - 1) * USERS_PER_PAGE + 1, sortedUsers.length)}
                      {" "}to{" "}
                      {Math.min(userPage * USERS_PER_PAGE, sortedUsers.length)}
                      {" "}of {sortedUsers.length} users
                    </div>

                    <Pagination className="admin-chat-pagination mb-0">
                      <Pagination.First
                        onClick={() => setUserPage(1)}
                        disabled={userPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => setUserPage((page) => Math.max(1, page - 1))}
                        disabled={userPage === 1}
                      />

                      {Array.from({ length: totalUserPages }, (_, index) => index + 1).map((page) => {
                        const isActive = page === userPage;
                        const shouldShow =
                          totalUserPages <= 7 ||
                          page === 1 ||
                          page === totalUserPages ||
                          Math.abs(page - userPage) <= 1;

                        if (!shouldShow) return null;

                        const showLeftEllipsis = page === userPage - 1 && page > 2;
                        const showRightEllipsis = page === userPage + 1 && page < totalUserPages - 1;

                        return (
                          <React.Fragment key={page}>
                            {showLeftEllipsis && <Pagination.Ellipsis disabled />}
                            <Pagination.Item active={isActive} onClick={() => setUserPage(page)}>
                              {page}
                            </Pagination.Item>
                            {showRightEllipsis && <Pagination.Ellipsis disabled />}
                          </React.Fragment>
                        );
                      })}

                      <Pagination.Next
                        onClick={() => setUserPage((page) => Math.min(totalUserPages, page + 1))}
                        disabled={userPage === totalUserPages}
                      />
                      <Pagination.Last
                        onClick={() => setUserPage(totalUserPages)}
                        disabled={userPage === totalUserPages}
                      />
                    </Pagination>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Bulk Message Section */}
          <Card className="border-0 shadow-sm filter-section-card mt-4" style={{ opacity: selectedUserIds.length === 0 ? 0.75 : 1, transition: 'opacity 0.25s ease' }}>
            <Card.Body>
              <h5 className="mb-3 text-muted small fw-bold text-uppercase">
                Send Message to Selected Users ({selectedUserIds.length})
              </h5>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted">Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={selectedUserIds.length === 0 ? "Select users from the table above to enable bulk messaging..." : "Type your group message here..."}
                  value={bulkMessage}
                  onChange={(e) => setBulkMessage(e.target.value)}
                  disabled={selectedUserIds.length === 0 || bulkSending}
                />
              </Form.Group>

              {bulkError && <Alert variant="danger" className="py-2 small">{bulkError}</Alert>}
              {bulkSuccess && <Alert variant="success" className="py-2 small">{bulkSuccess}</Alert>}

              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setSelectedUserIds([]);
                    setBulkMessage("");
                    setBulkSuccess("");
                    setBulkError("");
                  }}
                  disabled={bulkSending || selectedUserIds.length === 0}
                  className="filter-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSendBulkMessage}
                  disabled={bulkSending || !bulkMessage.trim() || selectedUserIds.length === 0}
                  className="filter-btn px-4"
                >
                  {bulkSending ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </>
      )}

      {/* Chat View */}
      {currentView === "chat" && (
        <>
          <Button
            variant="outline-secondary"
            className="mb-4"
            onClick={handleBackToUsers}
          >
            <FaArrowLeft className="me-2" /> Back to Users
          </Button>

          <div className="chat-shell" style={{ height: "75vh", display: "flex", flexDirection: "column" }}>
            <div className="chat-shell__header" style={{ flexShrink: 0 }}>
              <div>
                <div className="chat-shell__title">
                  {isHistoryView
                    ? `History: ${getSelectedUserName()} • ${getHistoryStaffName()}`
                    : chatMode === "group"
                      ? `${groupLabel} (Group Chat)`
                      : `Chat with ${getSelectedUserName()}`}
                </div>
                {/* <div className="chat-shell__subtitle">
                  {chatMode === "group"
                    ? `${groupMembers.length} members`
                    : selectedUser?.email ||
                      selectedUser?.user_name ||
                      "Student chat thread"}
                </div> */}
              </div>
              <span className={`chat-shell__status chat-shell__status--${socketStatus}`}>
                {socketStatus}
              </span>
            </div>
            {chatMode === "group" && groupMembers.length > 0 && (
              <div className="mb-3 text-muted small">

              </div>
            )}

            <div className="chat-messages border rounded p-3 mb-3" style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {historyLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center text-muted py-5">
                  No messages yet. Start the conversation.
                </div>
              ) : (
                messages.map((msg) => {
                  const isPrimarySender = isHistoryView
                    ? msg.sender_id === Number(historyStaffId)
                    : msg.sender_id === Number(senderId);
                  return (
                    <div
                      key={msg.chat_id || `${msg.sender_id}-${msg.sent_at}`}
                      className={`chat-bubble mb-3 p-3 rounded ${isPrimarySender
                        ? "bg-primary text-white ms-auto"
                        : "bg-light me-auto"
                        }`}
                      style={{ maxWidth: "70%" }}
                    >
                      <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {renderMessageWithLinks(msg.message)}
                      </p>
                      {/* Media rendering */}
                      {msg.url && (
                        <div className="mt-2">
                          {msg.url.match(/\.(mp4|webm|ogg)$/i) ? (
                            <video controls className="w-100 rounded" style={{ maxHeight: "200px" }}>
                              <source src={msg.url} type="video/mp4" />
                            </video>
                          ) : msg.url.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i) ? (
                            <img src={msg.url} alt="Attachment" className="w-100 rounded" style={{ maxHeight: "200px", objectFit: "cover" }} />
                          ) : (
                            <a href={msg.url} target="_blank" rel="noopener noreferrer" className="text-dark text-decoration-none border rounded p-2 d-inline-block bg-light">
                              📄 View Document
                            </a>
                          )}
                        </div>
                      )}
                      <div className="small mt-1" style={{ opacity: 0.8 }}>
                        {msg.sent_at ? format(new Date(msg.sent_at), "yyyy/MM/dd HH:mm") : format(new Date(), "yyyy/MM/dd HH:mm")} {getSenderLabel(msg)}
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={scrollRef} />
            </div>

            {!isHistoryView && selectedFile && (
              <div className="chat-shell__attachment" style={{ flexShrink: 0 }}>
                <div className="chat-shell__attachment-label">
                  Attachment: {selectedFile.name}
                </div>
                {selectedFileType.startsWith("image/") && (
                  <img src={selectedFilePreview} alt="Preview" />
                )}
                {selectedFileType.startsWith("video/") && (
                  <video src={selectedFilePreview} controls />
                )}
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={clearSelectedFile}
                >
                  Remove
                </Button>
              </div>
            )}

            {!isHistoryView && (
              <InputGroup className="chat-shell__composer" style={{ flexShrink: 0 }}>
                <Form.Control
                  as="textarea"
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type your message..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  style={{ resize: "none", minHeight: "38px", maxHeight: "150px" }}
                />
                <Form.Control
                  type="file"
                  ref={fileInputRef}
                  hidden
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={triggerFileInput}
                  style={{ position: "relative", zIndex: 10 }}
                >
                  <FaPaperclip />
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSend}
                  disabled={!draft.trim() && !selectedFile}
                >
                  Send
                </Button>
              </InputGroup>
            )}

            {errorMessage && <Alert variant="danger" className="mt-3">{errorMessage}</Alert>}
          </div>
        </>
      )}

      {/* Student Details View */}
      {currentView === "details" && resolvedSelectedUser && (
        <>
          <Button
            variant="outline-secondary"
            className="mb-4"
            onClick={handleBackToUsers}
          >
            <FaArrowLeft className="me-2" /> Back to Users
          </Button>

          <Row className="mb-4">
            <Col md={12}>
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-2">Student Details</h5>
                  <div className="text-muted small">
                    Name: {resolvedSelectedUser.name || resolvedSelectedUser.user_name || "N/A"}
                  </div>
                  <div className="text-muted small">
                    Email: {resolvedSelectedUser.email || "N/A"}
                  </div>
                  <div className="text-muted small">
                    Username: {resolvedSelectedUser.user_name || "N/A"}
                  </div>
                  <div className="text-muted small">
                    Phone: {resolvedSelectedUser.phone_number || "N/A"}
                  </div>
                  <div className="text-muted small">
                    Birthdate: {resolvedSelectedUser.birthdate ? format(new Date(resolvedSelectedUser.birthdate), "dd MMM yyyy") : "N/A"}
                  </div>
                  <div className="text-muted small">
                    Role: {resolvedSelectedUser.role?.role_name || "User"}
                  </div>
                  <div className="text-muted small">
                    End Date: {resolvedSelectedUser.end_date ? format(new Date(resolvedSelectedUser.end_date), "dd MMM yyyy") : "Not set"}
                  </div>
                  <div className="text-muted small mb-3">
                    Days Left: {getDaysLeft(resolvedSelectedUser.end_date)} days
                  </div>
                  <Button
                    variant="outline-success"
                    onClick={handleExtendClick}
                  >
                    Extend Subscription Days
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h5 className="mb-1">{groupLabel}</h5>
                      {/* <div className="text-muted small">
                        {groupMembers.length} members
                      </div> */}
                    </div>
                    <Badge bg="primary">Group</Badge>
                  </div>
                  {/* <div className="mt-3 text-muted small">
                    {groupMembers
                      .slice(0, 5)
                      .map((member) => member.name || member.user_name)
                      .join(", ")}
                    {groupMembers.length > 5 &&
                      ` +${groupMembers.length - 5} more`}
                  </div> */}
                  <Button
                    className="mt-3"
                    variant="outline-primary"
                    onClick={handleOpenGroupChat}
                  >
                    Group Chat
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-1">Individual Chat</h5>
                  <div className="text-muted small">
                    Chat with selected student only
                  </div>
                  <Button
                    className="mt-3"
                    variant="outline-primary"
                    onClick={handleOpenIndividualChat}
                  >
                    Individual Chat
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {isErishAdmin && (
            <Row className="mb-4">
              <Col md={12}>
                <Card className="shadow-sm border-primary">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h5 className="mb-1">View Chat History</h5>
                        <div className="text-muted small">
                          Select a staff member to view individual chat history.
                        </div>
                      </div>
                      <Badge bg="primary">Erish Admin</Badge>
                    </div>

                    <Row className="g-2 align-items-end">
                      {selectedCollege?.college_id ? (
                        <Col md={3}>
                          <Form.Label className="small text-muted">Staff type</Form.Label>
                          <Form.Select
                            value={staffScope}
                            onChange={(e) => setStaffScope(e.target.value)}
                          >
                            <option value="college">College Staff</option>
                            <option value="erish">Erish Staff</option>
                          </Form.Select>
                        </Col>
                      ) : (
                        <Col md={3}>
                          <Form.Label className="small text-muted">Staff type</Form.Label>
                          <Form.Control value="Erish Staff" disabled />
                        </Col>
                      )}
                      <Col md={4}>
                        <Form.Label className="small text-muted">Search staff</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><FaSearch /></InputGroup.Text>
                          <Form.Control
                            placeholder="Search by name or email..."
                            value={staffSearch}
                            onChange={(e) => setStaffSearch(e.target.value)}
                          />
                        </InputGroup>
                      </Col>
                      <Col md={4}>
                        <Form.Label className="small text-muted">Select staff</Form.Label>
                        <Form.Select
                          value={historyStaffId}
                          onChange={(e) => setHistoryStaffId(e.target.value)}
                          disabled={staffLoading || staffUsers.length === 0}
                        >
                          <option value="">Choose staff</option>
                          {staffUsers
                            .filter((user) =>
                              user.name?.toLowerCase().includes(staffSearch.toLowerCase()) ||
                              user.email?.toLowerCase().includes(staffSearch.toLowerCase()) ||
                              user.user_name?.toLowerCase().includes(staffSearch.toLowerCase())
                            )
                            .map((user) => (
                              <option key={user.user_id} value={user.user_id}>
                                {user.name || user.user_name} ({user.role?.role_name || "Staff"})
                              </option>
                            ))}
                        </Form.Select>
                      </Col>
                      <Col md={2} className="d-grid">
                        <Button
                          variant="primary"
                          onClick={handleOpenHistoryView}
                          disabled={!historyStaffId || staffLoading}
                        >
                          View History
                        </Button>
                      </Col>
                    </Row>

                    {staffLoading && (
                      <div className="mt-3 text-muted small">Loading staff list...</div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <Modal show={showExtendModal} onHide={() => setShowExtendModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Extend Subscription Days</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedUser && (
                <div>
                  <p><strong>Student:</strong> {selectedUser.name || selectedUser.user_name || "N/A"}</p>
                  <p><strong>Email:</strong> {selectedUser.email || "N/A"}</p>
                  <p>
                    <strong>Current End Date:</strong>{" "}
                    {selectedUser.end_date ? format(new Date(selectedUser.end_date), "dd MMM yyyy") : "Not set"}
                  </p>

                  <Form.Group className="mt-3">
                    <Form.Label>Extension Method</Form.Label>
                    <div className="d-flex gap-3 mb-3">
                      <Form.Check
                        type="radio"
                        label="Add Days (from Today)"
                        name="extensionMode"
                        id="chat-mode-days"
                        checked={extensionMode === "days"}
                        onChange={() => setExtensionMode("days")}
                      />
                      <Form.Check
                        type="radio"
                        label="Select End Date"
                        name="extensionMode"
                        id="chat-mode-date"
                        checked={extensionMode === "date"}
                        onChange={() => setExtensionMode("date")}
                      />
                    </div>

                    {extensionMode === "days" ? (
                      <>
                        <Form.Label>Days to Add (from Today)</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={extraDays}
                          onChange={(e) => setExtraDays(Math.max(1, parseInt(e.target.value) || 1))}
                        />
                      </>
                    ) : (
                      <>
                        <Form.Label>Select New End Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={targetDate}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={(e) => setTargetDate(e.target.value)}
                        />
                      </>
                    )}
                  </Form.Group>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowExtendModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleExtendSubmit}
                disabled={updatingSubscription || (extensionMode === "date" && !targetDate)}
              >
                {updatingSubscription ? "Updating..." : "Update Subscription"}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default AdminChat;
